import type RootState from 'app-components/utils/redux-store/rootState';
import { Set as ImmutableSet } from 'immutable';
import { createSelector } from 'reselect';
import { investmentQuotesV2Selectors, investmentQuotesV2Utils } from '../investment-quotes-v2';
import { SECURITIES_REDUCER_KEY, type SecurityProps } from './securitiesTypes';
import { getPrice, getUnknownSecurity } from './securitiesUtils';

type State = Pick<RootState, typeof SECURITIES_REDUCER_KEY>;

export const getStore = (state: State) => state?.[SECURITIES_REDUCER_KEY];

export const getResourcesById = (state: State) => getStore(state)?.resourcesById;
export const getLoadPending = (state: State) => getStore(state).loadPending;
export const getIsLoading = (state: State) => getStore(state).isLoading;
export const getError = (state: State) => getStore(state).error;
export const getLastSyncDate = (state: State) => getStore(state).lastSyncDate;

export const getResourceById = (state: State, id: string) => getResourcesById(state)?.get(id);

const getQuoteBySecurity = (security: SecurityProps | undefined, quotes: any) => {
  const symbol = security?.symbol || null;
  const quote = investmentQuotesV2Utils.getQuoteBySymbol(quotes, symbol);

  return quote || security || getUnknownSecurity();
};

export const getSymbols = createSelector(
  (state?: State, securities?: ReturnType<typeof getResourcesById>) => securities || (state && getResourcesById(state)),
  (securities) =>
    ImmutableSet().withMutations(
      (symbols) =>
        securities &&
        securities.reduce((set, security) => (security?.symbol ? set.add(security.symbol?.trim()) : set), symbols),
    ),
);

export const getQuotePricesBySecurityId = createSelector(
  getResourcesById,
  investmentQuotesV2Selectors.getResourcesById,
  (securities, quotes) =>
    securities.map((mySecurity) => {
      const quote = getQuoteBySecurity(mySecurity, quotes);
      return getPrice(quote);
    }),
);

// searches the list of manual investment holdings created by the user that matches the
// params of symbol and name because QCS randomly populated of the securities list
export const getSecurityBySymbolOrName = createSelector(
  getResourcesById,
  (_state: unknown, symbol: string | undefined) => symbol,
  (_state: unknown, _symbol: unknown, holdingName?: string) => holdingName,
  (holdings, symbol, holdingName) =>
    holdings.find((p) => p.name === holdingName) || holdings.find((p) => p.symbol === symbol),
);

export const getSecurityQuoteBySymbolAndName = createSelector(
  getResourcesById,
  (_state: unknown, symbol: string | undefined) => symbol,
  (_state: unknown, _symbol: unknown, holdingName?: string) => holdingName,
  (holdings, symbol, holdingName) => holdings.find((p) => p.name === holdingName && p.symbol === symbol),
);

export const getQuoteByLocalSecurityId = createSelector(
  (state: State, localSecurityId: string) => getResourceById(state, localSecurityId),
  investmentQuotesV2Selectors.getResourcesById,
  (security, quotes) => getQuoteBySecurity(security, quotes),
);
